import { Avatar, Divider, Skeleton, Stack, Typography } from '@mui/material'
import { SkeletonText } from 'utoniq-ui'

import {
  Maybe,
  PurchasableUserDetailFragment,
} from '@/clients/utoniq-core/schema'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  artistId: string
  loading: boolean
  hasNextPage: boolean
  purchasableUsers: Maybe<PurchasableUserDetailFragment[]>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const ConsoleItemInformationLimitUserTemplate: Template<State, Action> =
  ({ state, onFetchMore }) => {
    const { t } = useTranslation('consoleItemInformationLimitUser')
    const { routerPages, generatePath, router } = useRouter<{
      itemId: string
    }>()
    return (
      <ArtistThemeProvider artistId={state.artistId}>
        <ConsoleBaseLayout
          margin
          header={{
            back: true,
            defaultBackPage: generatePath(routerPages.ConsoleItemInformation, {
              itemId: router.query.itemId ?? '#',
            }),
            title: t('title'),
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 2 }}>
            {state.loading && !state.purchasableUsers ? (
              <>
                <SkeletonText variant="medium" width="20%" />
                <SkeletonText variant="medium" width="10%" />
              </>
            ) : (
              <>
                <Typography variant="h6">
                  {t('whoCanPurchase', '購入可能者')}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {state.purchasableUsers?.length ?? 0}
                  {t('person')}
                </Typography>
              </>
            )}
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{ py: 2 }}
          >
            {state.loading && !state.purchasableUsers ? (
              <>
                <SkeletonText variant="small" width="60%" />
                <SkeletonText variant="small" width="20%" />
              </>
            ) : (
              <>
                <Typography variant="overline" color="text.hint">
                  {t('whoCanPurchase', '購入可能者')}
                </Typography>
                <Typography variant="overline" color="text.hint">
                  {t('numberPurchase', '購入数 / 購入可能枚数')}
                </Typography>
              </>
            )}
          </Stack>
          <ScrollFetcher
            nodes={state.purchasableUsers?.map(purchasableUser => (
              <MemberCell
                purchasableUser={purchasableUser}
                key={purchasableUser.userId}
              />
            ))}
            componentProps={{ spacing: 0 }}
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            onFetchMore={onFetchMore}
            loadingComponent={
              <Stack>
                {Array.from(Array(8).keys()).map(key => (
                  <MemberSkeleton key={key} />
                ))}
              </Stack>
            }
          />
        </ConsoleBaseLayout>
      </ArtistThemeProvider>
    )
  }

type MemberCellProps = {
  purchasableUser: PurchasableUserDetailFragment
}
const MemberCell: Cell<MemberCellProps> = ({ purchasableUser }) => (
  <CellFrame>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ py: 2 }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar
          src={
            purchasableUser.user?.avatar?.smallUri ??
            purchasableUser.user?.avatar?.uri ??
            ''
          }
          sx={{ width: 28, height: 28 }}
        />
        <Typography variant="subtitle1">
          {purchasableUser.user?.displayName}
        </Typography>
      </Stack>
      <Typography variant="subtitle2">
        {purchasableUser.purchasedCount}/{purchasableUser.purchasableCount}
      </Typography>
    </Stack>
    <Divider />
  </CellFrame>
)

const MemberSkeleton: Cell = () => (
  <CellFrame>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ py: 2 }}
    >
      <Stack direction="row" spacing={2} alignItems="center" flex={1}>
        <Skeleton variant="circular" width={28} height={28} />
        <SkeletonText variant="medium" width="20%" />
      </Stack>
      <SkeletonText variant="subtitle1" width="10%" />
    </Stack>
    <Divider />
  </CellFrame>
)
